import {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCaretDown,
  faCaretUp,
  faSearch,
  faCheck,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { Loader } from "../../components/generics/Loader";
import "../../style/common/drop-down.scss";
import { useDebounce } from "../generics/useDebounce";
import { Arrow } from "../../assets/Icons/arrow";
import { ArrowUp } from "../../assets/Icons/ArrowUp";
import { clickElement, focusElemnt } from "../../utilityFunctions";
import { AutoSizer, CellMeasurer, CellMeasurerCache, List } from "react-virtualized";

const MultiSelectDropDown = forwardRef((props, ref) => {
  const {
    width,
    listWidth = width,
    name,
    label,
    isCompulsory,
    options,
    optionLimit = false,
    preSelectedValues = [],
    searchPlaceholder,
    disabled,
    remoteData = false,
    selectAllOption = false,
    direction = "down",
    defaultAllSelected = false,
    id,
    nextId,
    prevId,
    autoFocus = false,
    showCrossIcon = false,
    searchByCode = false,
    searchByName = ""
  } = props;
  const defaultValue = Object.assign([], preSelectedValues);
  const [preSelectedState, setPreSelectedState] = useState([]);
  const [showDrop, setShowDrop] = useState(false);
  const [focus, setFocus] = useState(false);
  const [blur, setBlur] = useState(true);
  const [list, setList] = useState(options);
  const [selectedDisplayValue, setSelectedDisplayValue] = useState("");
  const [selectedValues, setSelectedValues] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [remoteSearchInput, setRemoteSearchInput] = useState("");
  const [filteredLists, setFilteredLists] = useState(options);
  const [loading, setLoading] = useState(false);
  const dropRef = useRef(null);
  const searchInputRef = useRef(null);
  const debouncedSearchTerm = useDebounce(remoteSearchInput, 500);
  const debouncedSearchTerm2 = useDebounce(searchInput, 500);
  const [currentPage, setCurrentPage] = useState(1);
  const [allSelected, setAllSelected] = useState(defaultAllSelected);
  const [cursor, setCursor] = useState(-1);
  //const suggestions = searchInput !== "" ? filteredLists : list;
  const input1ref = useRef(null);
  const searchResultRef = useRef(null);

  const virtualizedListRef = useRef(null);
  const [listHeight, setListHeight] = useState(0);
  const [triggeredScroll, setTriggeredScroll] = useState(false);
  const listRef = useRef(null);


  const cache = useRef(
    new CellMeasurerCache({
      fixedWidth: true,
      defaultHeight: 30,
      minHeight: 30,
    })
  );

  useEffect(() => {
    if (autoFocus) {
      input1ref.current.focus();
    }
  }, []);
  useEffect(() => {
    if (cursor < 0 || cursor > filteredLists?.length || !searchResultRef) {
      return () => { };
    }
    if (
      searchResultRef &&
      searchResultRef.current &&
      searchResultRef.current.children
    ) {
      let listItems = Array.from(searchResultRef.current.children);
      listItems[cursor] && scrollIntoView(listItems[cursor].offsetTop - 30);
    }
  }, [cursor]);
  const scrollIntoView = (position) => {
    searchResultRef.current.parentNode.scrollTo({
      top: position,
      behavior: "smooth",
    });
  };
  useEffect(() => {
    setAllSelected(defaultAllSelected);
  }, [defaultAllSelected, options]);


  useEffect(() => {
    cache.current.clearAll();
    if (virtualizedListRef.current) {
      virtualizedListRef.current.recomputeRowHeights();
      virtualizedListRef.current.forceUpdateGrid(); // <- important
    }
  }, [filteredLists,]);//dropdownPos


  useEffect(() => {
    if (filteredLists.length === 0) {
      setListHeight(30);
    } else {
      let totalHeight = 0;
      for (let i = 0; i < filteredLists.length; i++) {
        totalHeight += cache.current.rowHeight({ index: i }) || 40;
      }

      if (selectAllOption) {
        totalHeight += 30
      }
      setListHeight(Math.max(30, Math.min(totalHeight, 200)));

    }
  }, [filteredLists, showDrop, selectAllOption]);
  useEffect(() => {
    if (preSelectedValues?.length === 0) {
      setSelectedDisplayValue("");
      setAllSelected(false);
    }
    if (
      preSelectedState?.length !== preSelectedValues?.length &&
      preSelectedValues?.length === 1
    ) {
      setAllSelected(false);
    }
    if (
      preSelectedState?.length !== preSelectedValues?.length &&
      preSelectedValues?.length > 1
    ) {
      setAllSelected(true);
    }
    if (preSelectedValues?.length === 1) {
      setAllSelected(false);
    }
    setPreSelectedState(preSelectedValues);
  }, [preSelectedValues]);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropRef]);

  useEffect(() => {
    const { options } = props;
    setList(options);

    setFilteredLists(options);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);


  const debounce = (func, delay) => {
    let timeoutId;
    return function (...args) {
      const context = this;
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func.apply(context, args), delay);
    };
  };

  function throttle(func, wait) {
    let lastTime = 0;
    return function (...args) {
      const now = new Date().getTime();
      if (now - lastTime >= wait) {
        lastTime = now;
        func.apply(this, args);
      }
    };
  }


  useEffect(() => {
    if (defaultValue?.length > 0 && options?.length > 0) {
      const { isObject, primaryKey } = props;
      if (isObject) {
        let selectedItems = [];
        for (let dv of defaultValue) {
          const selectedItem = list?.find(
            (l) => l[primaryKey] === dv[primaryKey]
          );
          if (selectedItem) {
            selectedItems.push(selectedItem);
          }
        }
        setSelectedValues(selectedItems);
      } else {
        setSelectedValues(defaultValue);
      }
    } else if (options?.length > 0) setSelectedValues(defaultValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, preSelectedState]);

  useEffect(() => {
    const { isObject, labelKey } = props;
    if (selectedValues?.length > 0) {
      if (isObject) {
        const selectedDisplayValuesArr = selectedValues.map(
          (item) => item?.[labelKey]
        );
        setSelectedDisplayValue(selectedDisplayValuesArr.join(", "));
      } else {
        setSelectedDisplayValue(selectedValues.join(", "));
      }
    } else {
      setSelectedDisplayValue("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedValues]);

  useEffect(
    () => {
      const { remoteData = false } = props;
      if (remoteData) {
        if (debouncedSearchTerm) {
          setLoading(true);
          fetchRemoteData(debouncedSearchTerm).then((options) => {
            setLoading(false);

            setFilteredLists(options);
          });
        } else {
          setFilteredLists([]);
          setLoading(false);
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [debouncedSearchTerm] // Only call effect if debounced search term changes
  );
  useEffect(() => {
    searchItems(debouncedSearchTerm2);
  }, [debouncedSearchTerm2]);
  const fetchRemoteData = async (searchValue) => {
    const {
      fetchRemoteDataHandler = () => {
        return [];
      },
    } = props;
    let newOptions = await fetchRemoteDataHandler(searchValue);
    return newOptions;
  };

  const handleClickOutside = (e) => {
    if (!(dropRef && dropRef.current && dropRef.current.contains(e.target))) {
      setShowDrop(false);
      setCurrentPage(1)
    }
  };

  const handleDrop = (e) => {
    setSearchInput("");
    setShowDrop(true);
    setFilteredLists(list);
  };

  const onRemoveSelectedItem = (item) => {
    let index = -1;
    const { onRemove, primaryKey, isObject } = props;
    if (isObject) {
      index = selectedValues.findIndex(
        (sv) => sv[primaryKey] === item[primaryKey]
      );
    } else {
      index = selectedValues.indexOf(item);
    }
    if (index >= 0) {
      selectedValues.splice(index, 1);
      if (onRemove) onRemove(selectedValues, item);

      setSelectedValues(Object.assign([], selectedValues));
    }
  };

  const onSelectItem = (e, item) => {
    const { onSelect = () => { }, singleSelect } = props;
    e.stopPropagation();
    if (singleSelect) {
      onSingleSelect(item);
      onSelect([item], item);
      setAllSelected(false);
      setFilteredLists(options);
      return;
    }
    if (isSelectedValue(item)) {
      onRemoveSelectedItem(item);
      return;
    }
    selectedValues.push(item);
    onSelect(selectedValues, item);
    setSelectedValues(Object.assign([], selectedValues));
  };

  const onSingleSelect = (item) => {
    const { isObject, labelKey } = props;
    setSelectedValues([item]);
    setSelectedDisplayValue(isObject ? item[labelKey] : item);
    setShowDrop(false);
    setCurrentPage(1)
  };

  const updateAll = async () => {
    const { onSelect = () => { } } = props;

    const updatedSelectedValues = [...options];

    setSelectedValues(updatedSelectedValues);

    onSelect(updatedSelectedValues);

    onSelect(updatedSelectedValues);
  };
  const onSelectAll = (e) => {
    setAllSelected(allSelected);
    setShowDrop(false);
    setCurrentPage(1)
    // setTimeout(() => {
    updateAll()
    // }, 0)
    // filteredLists.forEach((el) => {
    //   const isSelected = isSelectedValue(el);
    //   if (allSelected === isSelected) {
    //     if (isSelected) {
    //       onRemoveSelectedItem(el);
    //       return;
    //     }
    //     selectedValues.push(el);
    //     onSelect(selectedValues, el);
    //     setSelectedValues(Object.assign([], selectedValues));
    //   }
    // });


  };


  const searchItems = (searchValue) => {
    if (remoteData) {
      setRemoteSearchInput(searchValue);
      return;
    }
    setSearchInput(searchValue);

    if (searchValue !== "") {
      let filterData = [];

      // First try to filter by searchByName if provided
      if (searchByName) {
        filterData = list.filter((item) => {
          return item[props.searchByName].toLowerCase() === searchValue.toLowerCase();
        });
      }

      // If no results by searchByName, fallback to labelKey filter or searchByCode if specified
      if (filterData?.length === 0) {
        if (searchByCode) {
          filterData = list.filter((item) => {
            return item[props.labelKey]
              .toLowerCase()
              .includes(searchValue.toLowerCase());
          });
        } else {
          filterData = list.filter((item) => {
            return (item[props.labelKey] + '')
              .toLowerCase()
              .includes(searchValue.toLowerCase());
          });
        }
      }


      setFilteredLists(filterData);
      setCursor(0);
    } else {
      setFilteredLists(Object.assign([], list));
    }
  };

  const clearSearchInput = () => {
    if (searchInputRef.current) {
      searchInputRef.current.value = "";
      searchItems("");
    }
  };

  const resetItem = () => {
    const { onRemove } = props;
    setSelectedDisplayValue("");
    setSelectedValues([]);
    if (onRemove) onRemove([]);
    setAllSelected(false);
  };

  const isSelectedValue = (item) => {
    const { isObject, primaryKey } = props;
    if (isObject) {
      return (
        selectedValues.filter((i) => i[primaryKey] === item?.[primaryKey])
          ?.length > 0
      );
    }
    return selectedValues.filter((i) => i === item)?.length > 0;
  };

  useImperativeHandle(ref, () => ({
    resetAutocomplete(event) {
      resetItem();
    },
  }));

  const RowRenderer = ({ index, key, style, parent }) => {
    const {
      isObject,
      labelKey,
      primaryKey,
    } = props;

    if (selectAllOption && index === 0) {
      const allSelected =
        selectedValues.length === options.length;
      return (
        <CellMeasurer
          key={key}
          cache={cache.current}
          parent={parent}
          columnIndex={0}
          rowIndex={index}
        >
          {({ measure }) => (
            <li style={style}>
              <div
                key={"selAll"}
                className={`drop--item ${allSelected ? "selected" : ""} ${cursor === -1 ? "highlighted" : ""
                  }`}
                onClick={onSelectAll}
              >
                <div>Select All</div>
              </div>
            </li>
          )}
        </CellMeasurer>
      );
    }

    const actualIndex = selectAllOption
      ? index - 1
      : index;
    const obj = filteredLists[actualIndex];
    const isSelected = isSelectedValue(obj);
    const optionKey = isObject ? obj[primaryKey] : `${obj}-${index}`;
    const optionLabel = isObject ? obj[labelKey] : obj;

    return (
      <CellMeasurer
        key={key}
        cache={cache.current}
        parent={parent}
        columnIndex={0}
        rowIndex={index}
      >
        {({ measure }) => (
          <li
            key={key}
            style={{ ...style, whiteSpace: 'normal' }}
          >
            <div
              key={optionKey}
              className={`drop--item ${isSelected ? "selected" : ""} ${cursor === index ? "highlighted" : ""
                }`}
              onClick={(e) => onSelectItem(e, obj)}
            >

              <div onLoad={measure}>{optionLabel}</div>
            </div>
          </li>
        )}
      </CellMeasurer>
    )
  }



  const labelTxyt = isCompulsory ? (
    <span>
      {label}
      <span style={{ color: "red", paddingLeft: "5px" }}>*</span>
    </span>
  ) : (
    label
  );
  const showSuggestion = (e, el) => {
    e.stopPropagation();
    setShowDrop(true);
    searchItems('')
  };
  const hideSuggestion = (e, el) => {
    setShowDrop(false);
    setCurrentPage(1)
    searchItems('')

  };
  const focusNextElement = (e) => {
    focusElemnt(nextId);
    e.preventDefault();
  };
  const focusPrevElement = () => {
    focusElemnt(prevId);
  };

  const keyboardNavigation1 = (e) => {
    if (e.key === "ArrowDown" && !disabled) {

      showSuggestion(e);
    }
    if (e.key === "ArrowUp" && !disabled) {
      showSuggestion(e);
    }
    if (e.key === "Escape") {
      setShowDrop(false);
      focusPrevElement();
    }
    if (e.key === "Enter" || e.key === "Tab") {
      setShowDrop(false);
      focusNextElement(e);
    }
  };
  const keyboardNavigation2 = (e) => {
    let preCursor = cursor
    if (e.key === "ArrowDown") {
      preCursor = cursor < filteredLists.length - 1 ? cursor + 1 : -1
      showDrop
        ? setCursor(preCursor)
        : showSuggestion();

      if (virtualizedListRef.current) {
        virtualizedListRef.current.scrollToRow(preCursor);
      }
    }

    if (e.key === "ArrowUp") {
      preCursor = cursor > 0 ? cursor - 1 : -1
      setCursor(preCursor);
      if (virtualizedListRef.current) {
        virtualizedListRef.current.scrollToRow(preCursor);
      }

    }

    if (e.key === "Escape") {
      hideSuggestion();
      focusPrevElement();
    }
    if ((e.key === "Enter" || e.key === "Tab") && cursor > -1) {
      // setSelectedValues(suggestions[cursor][labelKey]);
      //  setInpVal(suggestions[cursor])
      const { onSelect = () => { }, singleSelect } = props;
      e.stopPropagation();
      const item = filteredLists[cursor];
      if (singleSelect) {
        onSingleSelect(item);
        onSelect([item], item);
        setAllSelected(false);
        setFilteredLists(options);
        focusNextElement(e)
        return;
      }
      if (isSelectedValue(item)) {
        onRemoveSelectedItem(item);
        return;
      }
      selectedValues.push(item);
      onSelect(selectedValues, item);
      setSelectedValues(Object.assign([], selectedValues));
      hideSuggestion();
      nextId ? focusNextElement(e) : input1ref.current.focus();
    }

    if ((preCursor === -1 && selectAllOption) && (e.key === "Enter" || e.key === "Tab")) {
      onSelectAll(e)
      nextId ? focusNextElement(e) : input1ref.current.focus();
      return;
    }
    if (preSelectedValues?.length === 1 && preCursor === -1 && (e.key === "Enter" || e.key === "Tab")) {
      hideSuggestion();
      nextId ? focusNextElement(e) : input1ref.current.focus();
    }
  };

  const rowCount = selectAllOption
    ? filteredLists.length + 1
    : filteredLists.length;
  return (
    <div
      className={`dropdown--wrapper ${disabled ? "disable" : ""}`}
      style={{ width: width }}
      ref={dropRef}
      onClick={handleDrop}
    >
      <input
        type="text"
        className={`inp--field ${disabled ? "disable" : ""} ${showDrop ? "focusColor" : ""
          }`}
        onFocus={(e) => {
          if (!disabled) {
            showSuggestion(e)
          }
        }}
        value={allSelected ? `All ${label}` : selectedDisplayValue}
        readOnly
        id={id}
        ref={input1ref}
        onKeyDown={keyboardNavigation1}
        onClick={showSuggestion}
      />
      {showCrossIcon && (selectedDisplayValue && !disabled) ? (
        <div className="cross--icon" onClick={resetItem}>
          <span className="cross" style={{ display: showCrossIcon ? 'block' : 'none' }}>
            <FontAwesomeIcon icon={faXmark} color={"#666666"} />
          </span>
        </div>
      ) : null}
      <div className="caret--icon">{showDrop ? <ArrowUp /> : <Arrow />}</div>
      <div
        className={`inp--label ${showDrop ? "focus--label" : ""} ${focus ? "active" : selectedDisplayValue && blur ? " filled" : ""
          }`}
      >
        {labelTxyt}
      </div>
      {showDrop && (
        <div
          className={`drop--wrapper ${direction === "up" ? "up" : "down"}`}
          style={{ width: listWidth }}
        >
          <div className="drop--search">
            <input
              name={name}
              type="text"
              className="search"
              placeholder={searchPlaceholder}
              onChange={(e) => searchItems(e.target.value)}
              onKeyDown={(e) => keyboardNavigation2(e)}
              ref={searchInputRef}
              autoFocus
              autoComplete={"off"}
            />
            <div className="search--icon">
              <FontAwesomeIcon icon={faSearch} color={"#777"} />
            </div>
            {(searchInput || remoteSearchInput) && (
              <div className="cross--icon" onClick={clearSearchInput}>
                <span className="cross">
                  <FontAwesomeIcon icon={faXmark} color={"#777"} />
                </span>
              </div>
            )}
          </div>
          <div className="drop--content" >
            <ul ref={listRef} className="list-group dropDwonScroll" style={{ ...(filteredLists.length > 0 ? { height: `${listHeight}px` } : { minHeight: '30px' }), overflowY: 'auto' }}>
              {filteredLists.length > 0 ? (
                <>

                  <AutoSizer >
                    {({ height, width }) => (
                      <List
                        ref={virtualizedListRef}
                        width={width - 4}
                        height={listHeight}
                        rowCount={rowCount}
                        rowHeight={cache.current.rowHeight}
                        rowRenderer={RowRenderer}
                        deferredMeasurementCache={cache.current}
                        overscanRowCount={5}
                        style={{ overflowX: 'hidden', }}
                        onRowsRendered={() => {
                          let totalHeight = 0;
                          for (let i = 0; i < filteredLists.length; i++) {
                            totalHeight += cache.current.getHeight(i, 0) || 40;
                          }
                          const maxHeight = 200;
                          if (selectAllOption) {
                            totalHeight += 30
                          }
                          setListHeight(Math.min(totalHeight, maxHeight));
                        }}
                      // scrollToIndex={triggeredScroll ? undefined : cursor}
                      />
                    )}
                  </AutoSizer>
                </>)
                :
                <li>
                  <div className="drop--item">
                    <div>No Data Found</div>
                  </div>
                </li>
              }
            </ul>
          </div>
        </div>
      )}
    </div>
  );
});

export default MultiSelectDropDown;
